import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, timeout } from 'rxjs/operators';
import { MessageService, SelectItemGroup } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

import { ActivityService, ProjectService, ProjectConfigurationService, UserService } from '#services/api';
import { ActivityCodeTypeService } from '#services/api/activityCodeType.service';
import { LoadingIndicatorService } from '#services/shared';
import {
  ComplianceReport,
  WbsSummary,
  ActivitySummary,
  ActivityCodeType,
  Project,
  PA_JOB_NUMBER
} from '#models/index';

interface ComplianceJobRow {
  key: string;
  name: string;
  wbsPath: string;
  PlannedLaborHours: number;
  EarnedLaborHours: number;
  ActivityCount: number;
  activities: ActivitySummary[];
}

interface ComplianceGroupRow {
  key: string;
  name: string;
  PlannedLaborHours: number;
  EarnedLaborHours: number;
  ActivityCount: number;
  activities: ActivitySummary[];
  jobs: ComplianceJobRow[];
}

@Component({
  selector: 'app-compliance-report',
  templateUrl: './compliance-report.component.html',
  styleUrls: ['./compliance-report.component.scss'],
  standalone: false
})
export class ComplianceReportComponent implements OnInit {

  currentProjectId: number;
  currentProject: Project;

  report: ComplianceReport;
  private allItems: WbsSummary[] = [];

  wbsCategoryFilterData: WbsSummary[] = [];
  selectedWbsCategories: WbsSummary[] = [];
  ewoOptions: any[] = [];
  selectedEwo: number[] = [];

  hoursOptions = [
    { label: '12 HRS', value: 12 },
    { label: '24 HRS', value: 24 },
    { label: '48 HRS', value: 48 }
    //{ label: 'ALL', value: 0 }
  ];

  // Guards against a non-responding backend leaving the global loading overlay stuck
  // forever: if the compliance report request does not resolve within this window the
  // observable errors out so finalize() hides the spinner and the error toast shows.
  private readonly requestTimeoutMs = 60000;
  selectedHours = 12;

  groupRows: ComplianceGroupRow[] = [];

  barData: any;
  barOptions: any;
  complianceData: any;
  complianceOptions: any;

  totalPlanned = 0;
  totalEarned = 0;
  // All-encompassing earned hours (every earned activity since the Publish Date /
  // Data Date), independent of the selected hours window and the WBS/EWO filters.
  // Only the top "Total Earned Hours" summary card binds to this value.
  totalEarnedAllTime = 0;
  compliancePercent = 0;

  showCategoryDetail = false;
  selectedCategory: ComplianceGroupRow;
  categorySearch = '';
  // Tracks which Work Package rows are expanded in the category detail popup, keyed
  // by ComplianceJobRow.key. Managed explicitly so the drill-down works reliably
  // inside the p-dialog.
  expandedJobKeys: { [key: string]: boolean } = {};

  // Sort state for the activities table inside the category detail popup. Supports
  // sorting by Activity Id, Planned Hours and Earned Hours.
  activitySortField: 'ActivityId' | 'PlannedLaborHours' | 'EarnedLaborHours' = 'ActivityId';
  activitySortOrder = 1;

  get isFromTapro(): boolean {
    return this.userSvc.isFromTapro();
  }

  constructor(
    private route: ActivatedRoute,
    private activitySvc: ActivityService,
    private projectSvc: ProjectService,
    private configSvc: ProjectConfigurationService,
    private activityCodeTypeSvc: ActivityCodeTypeService,
    private loadingIndicatorSvc: LoadingIndicatorService,
    private messageSvc: MessageService,
    private router: Router,
    private translate: TranslateService,
    private userSvc: UserService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.currentProjectId = params.projectId ? Number(params.projectId) : null;
      if (!this.currentProjectId) {
        this.router.navigate(['error/not-found']);
        return;
      }
      this.projectSvc.getProject(this.currentProjectId).subscribe(data => {
        this.currentProject = data;
        this.cdr.detectChanges();
      });
      this.loadEwoOptions();
      this.loadTotalEarned();
      this.loadReport();
    });
  }

  loadEwoOptions(): void {
    // The Activity Code Types page saves which code type represents the project's EWO
    // (config['PA Job Number']). Use that code type's scope to populate this dropdown
    // with the matching ActivityCode descriptions.
    this.configSvc.getProjectConfig(this.currentProjectId).subscribe({
      next: (config: any) => {
        const ewoCodeType: ActivityCodeType | null = config ? config[PA_JOB_NUMBER] : null;
        this.loadEwoActivityCodes(ewoCodeType);
      },
      error: (e) => {
        this.ewoOptions = [];
        this.cdr.detectChanges();
        this.showError(e, this.translate.instant('COMPLIANCEREPORT.FailedJobs'));
      }
    });
  }

  private loadEwoActivityCodes(ewoCodeType: ActivityCodeType | null): void {
    if (!ewoCodeType?.Id) {
      this.ewoOptions = [];
      this.cdr.detectChanges();
      return;
    }
    // The individual EWO values (e.g. "#1 Disassemble") live in the ActivityCode
    // table as Descriptions belonging to the configured EWO code type. Fetch those
    // codes for the selected code type and list their descriptions in the dropdown.
    this.activityCodeTypeSvc.getActivityCodes(this.currentProjectId, ewoCodeType.Id).subscribe({
      next: (codes: any[]) => {
        this.ewoOptions = this.buildEwoOptions(ewoCodeType, codes || []);
        this.cdr.detectChanges();
      },
      error: (e) => {
        this.ewoOptions = [];
        this.cdr.detectChanges();
        this.showError(e, this.translate.instant('COMPLIANCEREPORT.FailedJobs'));
      }
    });
  }

  private buildEwoOptions(ewoCodeType: ActivityCodeType, codes: any[]): any[] {
    return (codes || []).map(code => ({
      label: code.Description || code.CodeValue || code.Name,
      value: code.Id
    }));
  }

  private groupByCodeType(data: ActivityCodeType[]): SelectItemGroup[] {
    const groups: { [key: string]: SelectItemGroup } = {};
    data.forEach(codeType => {
      groups[codeType.Scope] = groups[codeType.Scope] || { label: this.convertScopeType(codeType.Scope), value: codeType.Scope, items: [] };
      groups[codeType.Scope].items.push({ label: codeType.Description || codeType.Name, value: Object.assign({}, codeType) });
    });
    const result: SelectItemGroup[] = [];
    for (const key in groups) {
      if (groups.hasOwnProperty(key)) {
        result.push(groups[key]);
      }
    }
    return result;
  }

  private convertScopeType(type: string): string {
    return type + this.translate.instant('PROJECT.Scope');
  }

  onHoursChange(): void {
    this.loadReport();
  }

  // Loads the all-encompassing earned hours (hours = 0 => no window) once per
  // project. This value is independent of the selected hours window, so it is not
  // reloaded when the window changes.
  private loadTotalEarned(): void {
    this.activitySvc.getComplianceReport(this.currentProjectId, 0).pipe(
      timeout(this.requestTimeoutMs)).subscribe({
      next: (data: ComplianceReport) => {
        this.totalEarnedAllTime = this.round(
          this.sumActivities(this.collectActivities(data?.WbsSummaries || []), 'EarnedLaborHours'));
        this.cdr.detectChanges();
      },
      error: () => {
        this.totalEarnedAllTime = 0;
        this.cdr.detectChanges();
      }
    });
  }

  loadReport(): void {
    this.loadingIndicatorSvc.show();
    this.activitySvc.getComplianceReport(this.currentProjectId, this.selectedHours).pipe(
      timeout(this.requestTimeoutMs),
      finalize(() => this.loadingIndicatorSvc.hide()))
      .subscribe({
        next: (data: ComplianceReport) => {
          this.report = data;
          this.allItems = data?.WbsSummaries || [];
          this.wbsCategoryFilterData = this.allItems;
          // Windowed earned hours (scoped to the selected hours window) feeding the
          // compliance doughnut/percentage. The top "Total Earned Hours" card uses the
          // all-encompassing totalEarnedAllTime instead. Not affected by WBS/EWO filters.
          this.totalEarned = this.round(
            this.sumActivities(this.collectActivities(this.allItems), 'EarnedLaborHours'));
          this.applyFilters();
          this.cdr.detectChanges();
        },
        error: (e) => {
          this.report = null;
          this.allItems = [];
          this.groupRows = [];
          this.totalEarned = 0;
          this.wbsCategoryFilterData = [];
          this.cdr.detectChanges();
          this.showError(e, this.translate.instant('COMPLIANCEREPORT.FailedReport'));
        }
      });
  }

  applyFilters(): void {
    const categoryIds = this.selectedWbsCategories?.map(c => c.WbsCategoryId) || [];

    // Build the filtered category tree
    const filteredTree: WbsSummary[] = (this.allItems || [])
      .filter(cat => !categoryIds.length || categoryIds.includes(cat.WbsCategoryId));

    this.groupRows = this.buildGroupRows(filteredTree);
    this.buildCharts();
  }

  clearFilters(): void {
    this.selectedWbsCategories = [];
    this.selectedEwo = [];
    this.applyFilters();
  }

  private buildGroupRows(tree: WbsSummary[]): ComplianceGroupRow[] {
    return tree.map(cat => {
      // All activities (including zero-labor milestones) drive the displayed list and
      // ActivityCount so the count matches TA Pro / the backend response.
      const activities = this.collectActivities([cat]);
      // Planned/Earned hours are summed only from non-milestone activities
      // (Budgeted Labour Units > 0) so the totals stay consistent with the report.
      const budgetedActivities = activities.filter(act => this.hasBudgetedLabourUnits(act));
      const plannedHours = budgetedActivities.length ? this.sumActivities(budgetedActivities, 'PlannedLaborHours') : (cat.PlannedLaborHours || 0);
      const earnedHours = budgetedActivities.length ? this.sumActivities(budgetedActivities, 'EarnedLaborHours') : (cat.EarnedLaborHours || 0);
      return {
        key: `cat-${cat.WbsCategoryId}`,
        name: cat.WbsCategoryName,
        PlannedLaborHours: plannedHours,
        EarnedLaborHours: earnedHours,
        ActivityCount: activities.length || cat.ActivityCount || 0,
        activities,
        jobs: this.buildJobRows(cat)
      };
    });
  }

  // Builds the Work Package (Job) rows shown inside the category detail popup so the
  // popup can render Category -> Work Packages -> (expand) Activities, mirroring TA Pro.
  // Each Work Package lists all of its activities (matching the TA Pro activity count),
  // but the Planned/Earned/Compliance sums are computed only from non-milestone
  // activities (Budgeted Labour Units > 0) so the totals match the rest of the report.
  private buildJobRows(cat: WbsSummary): ComplianceJobRow[] {
    const jobs = (cat?.Jobs || []).map(job => {
      const activities = job.Activities || [];
      const budgetedActivities = activities.filter(act => this.hasBudgetedLabourUnits(act));
      return {
        key: `job-${cat.WbsCategoryId}-${job.JobId}`,
        name: job.JobName,
        wbsPath: this.resolveWbsPath(cat, job.WbsPath || activities[0]?.WbsPath, job.JobName),
        PlannedLaborHours: this.sumActivities(budgetedActivities, 'PlannedLaborHours'),
        EarnedLaborHours: this.sumActivities(budgetedActivities, 'EarnedLaborHours'),
        ActivityCount: activities.length,
        activities
      };
    });

    // Activities attached directly to the category (not under a Job) are grouped by
    // their WbsName, which is the Work Package (deepest WBS level) each activity
    // belongs to, e.g. "CU321-M1 - D-1135 Medium Pressure Steam Drum Recertification".
    const looseActivities = cat?.Activities || [];
    jobs.push(...this.groupLooseActivitiesByWbs(cat, looseActivities));

    return jobs;
  }

  private groupLooseActivitiesByWbs(cat: WbsSummary, activities: ActivitySummary[]): ComplianceJobRow[] {
    const groups = new Map<string, ActivitySummary[]>();
    const unassignedLabel = this.translate.instant('COMPLIANCEREPORT.Unassigned');
    (activities || []).forEach(act => {
      const packageName = (act.WbsName || '').trim() || unassignedLabel;
      const bucket = groups.get(packageName) || [];
      bucket.push(act);
      groups.set(packageName, bucket);
    });

    const rows: ComplianceJobRow[] = [];
    groups.forEach((acts, packageName) => {
      const budgetedActs = acts.filter(act => this.hasBudgetedLabourUnits(act));
      rows.push({
        key: `wbs-${cat.WbsCategoryId}-${packageName}`,
        name: packageName,
        wbsPath: this.resolveWbsPath(cat, acts[0]?.WbsPath, packageName),
        PlannedLaborHours: this.sumActivities(budgetedActs, 'PlannedLaborHours'),
        EarnedLaborHours: this.sumActivities(budgetedActs, 'EarnedLaborHours'),
        ActivityCount: acts.length,
        activities: acts
      });
    });
    return rows.sort((a, b) => a.name.localeCompare(b.name));
  }

  // TODO: remove once backend returns real earned hours. Generates a deterministic
  // dummy earned value (55%-95% of planned) so the chart/table are not all zeros.
  // private dummyEarned(seed: number, planned: number): number {
  //   if (!planned) {
  //     return 0;
  //   }
  //   const ratio = 0.55 + ((seed % 41) / 100);
  //   return this.round(planned * ratio);
  // }

  private collectActivities(categories: WbsSummary[]): ActivitySummary[] {
    const activities: ActivitySummary[] = [];
    (categories || []).forEach(cat => {
      (cat.Activities || []).forEach(act => activities.push(act));
      (cat.Jobs || []).forEach(job => {
        (job.Activities || []).forEach(act => activities.push(act));
      });
    });
    // All activities are returned (including zero Budgeted Labour Units milestones) so
    // ActivityCount matches the backend. Milestones carry 0 planned/earned hours, so
    // they do not affect Planned Hours, Earned Hours, or Lookahead Compliance sums.
    return activities;
  }

  private hasBudgetedLabourUnits(activity: ActivitySummary): boolean {
    return (activity?.PlannedLaborHours || 0) > 0;
  }

  private sumActivities(items: ActivitySummary[], key: 'PlannedLaborHours' | 'EarnedLaborHours'): number {
    return (items || []).reduce((total, item) => total + (item[key] || 0), 0);
  }

  private buildCharts(): void {
    const labels = this.groupRows.map(g => g.name);
    const planned = this.groupRows.map(g => this.round(g.PlannedLaborHours));
    const earned = this.groupRows.map(g => this.round(g.EarnedLaborHours));
 this.totalEarned = this.round(
  this.sumActivities(
    this.collectActivities(this.allItems),
    'EarnedLaborHours'
  )
);
    this.totalPlanned = this.round(planned.reduce((a, b) => a + b, 0));
    this.compliancePercent = this.totalPlanned > 0
      ? this.round((this.totalEarnedAllTime / this.totalPlanned) * 100)
      : 0;

    const axisColor = '#FFFFFF';
    const gridColor = 'rgba(255, 255, 255, 0.15)';

    this.barData = {
      labels,
      datasets: [
        {
          label: this.translate.instant('COMPLIANCEREPORT.PlannedHours'),
          backgroundColor: '#2E75B6',
          hoverBackgroundColor: '#3A86CC',
          data: planned,
          categoryPercentage: 0.7,
          barPercentage: 0.9
        },
        {
          label: this.translate.instant('COMPLIANCEREPORT.EarnedHours'),
          backgroundColor: '#FF0000',
          hoverBackgroundColor: '#FF3333',
          data: earned,
          categoryPercentage: 0.7,
          barPercentage: 0.9
        }
      ]
    };

    this.barOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
          title: {
            display: true,
            text: this.translate.instant('COMPLIANCEREPORT.Values'),
            color: axisColor,
            font: { weight: 'bold' }
          },
          labels: { color: axisColor, boxWidth: 14 }
        },
        tooltip: { mode: 'index', intersect: false }
      },
      scales: {
        x: {
          ticks: {
            color: axisColor,
            autoSkip: false,
            maxRotation: 30,
            minRotation: 0,
            callback: (value: any) => this.wrapLabel(labels[value])
          },
          grid: { color: gridColor, display: false }
        },
        y: {
          beginAtZero: true,
          ticks: { color: axisColor },
          grid: { color: gridColor }
        }
      }
    };

    const remaining = Math.max(this.totalPlanned - this.totalEarned, 0);
    this.complianceData = {
      labels: [
        this.translate.instant('COMPLIANCEREPORT.EarnedHours'),
        this.translate.instant('COMPLIANCEREPORT.RemainingHours')
      ],
      datasets: [
        {
          data: [this.round(this.totalEarned), this.round(remaining)],
          backgroundColor: ['#4CAF50', '#DBDBDB'],
          hoverBackgroundColor: ['#4CAF50', '#DBDBDB']
        }
      ]
    };

    this.complianceOptions = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '65%',
      plugins: {
        legend: { position: 'bottom', labels: { color: '#495057' } }
      }
    };
  }

  private round(value: number): number {
    return Math.round((value || 0) * 100) / 100;
  }

  // Split a long axis label into multiple lines so the text does not overlap.
  private wrapLabel(label: string, maxCharsPerLine = 14): string[] {
    if (!label) {
      return [''];
    }
    const words = label.split(' ');
    const lines: string[] = [];
    let current = '';
    words.forEach(word => {
      if ((current + ' ' + word).trim().length > maxCharsPerLine) {
        if (current) {
          lines.push(current.trim());
        }
        current = word;
      } else {
        current = (current + ' ' + word).trim();
      }
    });
    if (current) {
      lines.push(current.trim());
    }
    return lines;
  }

  openCategoryDetail(item: ComplianceGroupRow): void {
    this.selectedCategory = item;
    this.categorySearch = '';
    this.expandedJobKeys = {};
    this.showCategoryDetail = true;
  }

  toggleJobRow(job: { key: string }): void {
    if (this.expandedJobKeys[job.key]) {
      delete this.expandedJobKeys[job.key];
    } else {
      this.expandedJobKeys[job.key] = true;
    }
    // Reassign so the p-table picks up the change reference.
    this.expandedJobKeys = { ...this.expandedJobKeys };
  }

  // Toggles the sort field/direction for the activities table in the detail popup.
  sortActivities(field: 'ActivityId' | 'PlannedLaborHours' | 'EarnedLaborHours'): void {
    if (this.activitySortField === field) {
      this.activitySortOrder = this.activitySortOrder * -1;
    } else {
      this.activitySortField = field;
      this.activitySortOrder = 1;
    }
  }

  // Returns a Work Package's activities sorted by the currently selected column.
  // Activity Id sorts alphanumerically; Planned/Earned hours sort numerically.
  sortedActivities(job: ComplianceJobRow): ActivitySummary[] {
    const activities = [...(job?.activities || [])];
    const field = this.activitySortField;
    const order = this.activitySortOrder;
    return activities.sort((a, b) => {
      let result: number;
      if (field === 'ActivityId') {
        result = (a.ActivityId || '').localeCompare(b.ActivityId || '', undefined, { numeric: true });
      } else {
        result = (a[field] || 0) - (b[field] || 0);
      }
      return result * order;
    });
  }

  // Work Packages shown in the popup, filtered by the header search box (matches the
  // Work Package name or any of its activity ids/names).
  get filteredJobs(): ComplianceJobRow[] {
    const jobs = this.selectedCategory?.jobs || [];
    const term = (this.categorySearch || '').trim().toLowerCase();
    if (!term) {
      return jobs;
    }
    return jobs.filter(job =>
      (job.name || '').toLowerCase().includes(term) ||
      (job.activities || []).some(act =>
        (act.ActivityId || '').toLowerCase().includes(term) ||
        (act.ActivityName || '').toLowerCase().includes(term)));
  }

  categoryCompliance(planned: number, earned: number): number {
    return planned > 0 ? this.round((earned / planned) * 100) : 0;
  }

  // Resolves the WBS breadcrumb path for a Work Package. Prefers the full path
  // supplied by the backend (on the job or its activities); when that is missing it
  // falls back to the two levels we can build from the report tree
  // (WBS Category -> Work Package) so the breadcrumb always renders.
  private resolveWbsPath(cat: WbsSummary, backendPath: string, packageName: string): string {
    const path = (backendPath || '').trim();
    if (path) {
      return path;
    }
    const category = (cat?.WbsCategoryName || '').trim();
    const pkg = (packageName || '').trim();
    return [category, pkg].filter(segment => !!segment).join(' \u2192 ');
  }

  // Splits the activity WbsPath into its individual WBS levels so the popup can render
  // the breadcrumb, e.g. "H2 Train 1 -> H2 Train 1 - Work Scope Activities -> ...".
  // Only splits on the dedicated path delimiters (the arrow character, '->', '>>', '»',
  // '|' or a bare '>') so segment text that legitimately contains '/', '.' or '-'
  // (e.g. "H2 Train 1 - Drums / Vessels") is preserved. The multi-character delimiters
  // are listed before the bare '>' so "->" is consumed whole instead of leaving a
  // trailing hyphen on the previous segment.
  wbsBreadcrumb(path: string): string[] {
    if (!path) {
      return [];
    }
    return path
      .split(/\s*(?:\u2192|->|>>|»|\||>)\s*/)
      .map(segment => segment.trim())
      .filter(segment => !!segment);
  }

  onBackToDeltaReport(): void {
    this.router.navigate([''], { queryParams: { projectObjectId: this.currentProjectId } });
  }

  private showError(e: any, message: string): void {
    this.messageSvc.add({
      severity: 'error',
      summary: message,
      detail: e?.error?.Message || e?.message || ''
    });
  }
}
